import React, { useEffect, useState, useCallback } from "react";
import "./NewInvestigator.css";
import "../study_member.css";
import { FaUserPlus, FaSearch, FaHourglassHalf } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { study_membersService } from "../../../service";
import { toast } from "react-toastify";

const NewInvestigator = ({ studyId, onBack }) => {
  const [availableMembers, setAvailableMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPending, setLoadingPending] = useState(true);
  const [inviting, setInviting] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("name");
  const [error, setError] = useState("");
  const [emailInvite, setEmailInvite] = useState("");

  // ------------------------------
  // Fetch available members
  // ------------------------------
  const fetchAvailableMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await study_membersService.getAvailableUsers(studyId);
      setAvailableMembers(data || []);
    } catch (err) {
      console.error("Erreur lors du chargement des membres disponibles:", err);
      setError("Impossible de charger la liste des utilisateurs disponibles.");
    } finally {
      setLoading(false);
    }
  }, [studyId]);
  // ------------------------------
  // Fetch pending invitations
  // ------------------------------
  const fetchPendingInvites = useCallback(async () => {
    try {
      setLoadingPending(true);
      const { data } = await study_membersService.getPendingInvitations(studyId);
      setPendingInvites(data || []);
    } catch (err) {
      console.error("Erreur lors du chargement des invitations en attente:", err);
      toast.error("Impossible de charger les invitations en attente.");
    } finally {
      setLoadingPending(false);
    }
  }, [studyId]); 

  // ------------------------------
  // Load data on mount / study change
  // ------------------------------
  useEffect(() => {
    if (studyId) {
      fetchAvailableMembers();
      fetchPendingInvites();
    }
  }, [studyId, fetchAvailableMembers, fetchPendingInvites]);


  // ------------------------------
  // Invite logic
  // ------------------------------
  const handleInvite = async (email) => {
    if (!email || !email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      toast.warning("Veuillez saisir une adresse e-mail valide.");
      return;
    }

    try {
      setInviting(email);
      const { data } = await study_membersService.inviteInvestigator(studyId, email);
      toast.success(data.detail || `Invitation envoyée à ${email}`);

      // Refresh both lists after inviting (backend handles pending filtering)
      await fetchAvailableMembers();
      await fetchPendingInvites();
      setEmailInvite("");
    } catch (err) {
      console.error("Erreur lors de l’envoi de l’invitation :", err);
      const msg =
        err.response?.data?.detail ||
        "Une erreur est survenue lors de l’envoi de l’invitation.";
      toast.error(msg);
    } finally {
      setInviting(null);
    }
  };

  // ------------------------------
  // Filter search locally (optional UX)
  // ------------------------------
  const displayedMembers = availableMembers.filter((m) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    if (filterBy === "name") {
      return `${m.first_name} ${m.last_name}`.toLowerCase().includes(term);
    }
    if (filterBy === "email") {
      return m.email.toLowerCase().includes(term);
    }
    return false;
  });

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <div className="px-0 py-2">
      <div className="container-fluid">
        {/* Back + Search Bar */}
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center back-arrow pb-3"
            onClick={onBack}
            style={{ cursor: "pointer" }}
          >
            <FontAwesomeIcon icon={faCircleArrowLeft} />
          </div>
          <div className="search-bar-details flex-grow-1 d-flex align-items-center justify-content-between">
            <div className="search-group">
              <input
                type="text"
                placeholder="Chercher un investigateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="icon-btn">
                <FaSearch />
              </button>
            </div>
            <div className="select-group">
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
              >
                <option value="name">Par nom</option>
                <option value="email">Par email</option>
              </select>
            </div>
          </div>
        </div>

        {/* Invite by email input */}
        <div className="mb-4 mt-2">
          <h6 className="fw-semibold mb-3">Inviter un nouveau membre</h6>
          <div className="invite-email-section row d-flex align-items-center justify-content-center w-100 mx-1 pe-3 py-2">
            <input
              type="email"
              className="w-75 input-invite"
              placeholder="Saisir l'adresse e-mail du médecin..."
              value={emailInvite}
              onChange={(e) => setEmailInvite(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInvite(emailInvite)}
            />
            <button
              className="btn btn-invite w-25 d-flex align-items-center justify-content-center"
              onClick={() => handleInvite(emailInvite)}
              disabled={inviting === emailInvite}
            >
              {inviting === emailInvite ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                  Envoi...
                </>
              ) : (
                <>
                  <FaUserPlus className="me-1" /> Inviter
                </>
              )}
            </button>
          </div>
        </div>

        {/* Pending Invitations */}
        <div className="overflow-y-auto d-flex justify-content-between align-items-center mt-4">
          <h6 className="fw-semibold mb-3">Invitations en attente</h6>
        </div>
        {loadingPending ? (
          <p className="text-muted">Chargement des invitations en attente...</p>
        ) : pendingInvites.length === 0 ? (
          <p className="text-muted">Aucune invitation en attente.</p>
        ) : (
          <div className="list-group mb-4">
            {pendingInvites.map((invite, index) => (
              <div
                key={index}
                className="list-group-item d-flex justify-content-between align-items-center"
              >
                <div>
                  <strong>{invite.email}</strong>
                </div>
                <span className="badge-pending d-flex align-items-center gap-1">
                  <FaHourglassHalf /> En attente
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Available Members */}
        <div className="overflow-y-auto d-flex justify-content-between align-items-center mt-3">
          <h6 className="fw-semibold mb-3">Membres disponibles</h6>
        </div>
        {loading ? (
          <p className="text-muted">Chargement des membres disponibles...</p>
        ) : error ? (
          <div className="text-danger">{error}</div>
        ) : displayedMembers.length === 0 ? (
          <p className="text-muted">Aucun utilisateur disponible à inviter.</p>
        ) : (
          <div className="list-group">
            {displayedMembers.map((member) => (
              <div
                key={member.id}
                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
              >
                <div>
                  <div className="fw-bold">
                    {member.first_name} {member.last_name}
                  </div>
                  <small className="text-muted">{member.email}</small>
                </div>
                <button
                  className="btn btn-invite w-25"
                  onClick={() => handleInvite(member.email)}
                  disabled={inviting === member.email}
                >
                  {inviting === member.email ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Envoi...
                    </>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center">
                      <FaUserPlus className="me-1" />
                      Inviter
                    </div>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewInvestigator;
